import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firestore";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { useUser } from "../context/userContext";
import { useAuthContext } from "../context/AuthContext";

const Training = () => {
  const [trainings, setTrainings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userGifts, setUserGifts] = useState([]);
  const [userCourses, setUserCourses] = useState([]);
  const [userCoursesAccess, setUserCoursesAccess] = useState([]);
  const [userTrainings, setUserTrainings] = useState([]);
  const [userTrainingsAccess, setUserTrainingsAccess] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const giftItemIdsRef = useRef(new Set());
  const [giftItemIdsReady, setGiftItemIdsReady] = useState(false);
  const navigate = useNavigate();

  // Получаем данные из контекстов
  const { 
    id: userId, 
    loading: userLoading,
    userType
  } = useUser();
  
  const { user: firebaseUser } = useAuthContext();

  // Принудительная очистка кэша при загрузке страницы
  useEffect(() => {
    const clearAppCache = async () => {
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('training') || key.includes('course') || key.includes('userData') || key.includes('premium'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          console.log(`🗑️ Удален кэш: ${key}`);
        });

        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.includes('training') || key.includes('course') || key.includes('userData') || key.includes('premium'))) {
            sessionKeysToRemove.push(key);
          }
        }
        sessionKeysToRemove.forEach(key => {
          sessionStorage.removeItem(key);
          console.log(`🗑️ Удален session кэш: ${key}`);
        });

        if ('caches' in window) {
          const cacheNames = await caches.keys();
          for (const cacheName of cacheNames) {
            if (cacheName.includes('training') || cacheName.includes('course')) {
              await caches.delete(cacheName);
              console.log(`🗑️ Удален Cache Storage: ${cacheName}`);
            }
          }
        }

        console.log('✅ Кэш приложения очищен');
      } catch (error) {
        console.error('Ошибка при очистке кэша:', error);
      }
    };

    clearAppCache();
  }, []);

  // Принудительное обновление Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        for (let registration of registrations) {
          registration.update();
          console.log('🔄 Service Worker обновлен');
        }
      });
    }
  }, []);

  // Telegram WebApp инициализация (только для Telegram пользователей)
  useEffect(() => {
    if (userType === 'telegram' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      
      tg.ready();
      tg.expand();
      
      tg.BackButton.offClick();
      tg.BackButton.isVisible = false;
      
      setTimeout(() => {
        tg.BackButton.onClick(() => {
          console.log('Back button clicked in Training');
          navigate(-1);
        });
        tg.BackButton.isVisible = true;
      }, 50);
    }

    return () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.BackButton.offClick();
        tg.BackButton.isVisible = false;
      }
    };
  }, [navigate, userType]);

  useEffect(() => {
    const handleFocus = () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.BackButton.offClick();
        tg.BackButton.onClick(() => {
          console.log('Back button clicked in Training (focus)');
          navigate(-1);
        });
        tg.BackButton.isVisible = true;
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [navigate, userType]);

  // Получение имени коллекции в зависимости от типа пользователя
  const getCollectionName = () => {
    if (userType === 'web') {
      return 'webUsers';
    }
    return 'telegramUsers';
  };

  // Загрузка подарков пользователя
  const fetchUserGifts = async () => {
    if (!userId) return [];

    try {
      const collectionName = getCollectionName();
      const giftsRef = collection(db, collectionName, userId, "gifts");
      const q = query(giftsRef, where("status", "==", "active"));
      const querySnapshot = await getDocs(q);
      
      const gifts = [];
      const giftIds = new Set();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const expiresAt = data.expiresAt?.toDate?.() || new Date(data.expiresAt);
        const now = new Date();
        
        if (expiresAt > now) {
          const gift = {
            id: doc.id,
            itemId: data.itemId,
            itemType: data.itemType,
            ...data,
            expiresAt: expiresAt
          };
          gifts.push(gift);
          giftIds.add(data.itemId);
          console.log(`🎁 Подарок: ID=${data.itemId}, тип=${data.itemType}, название=${data.itemTitle}`);
        }
      });
      
      console.log(`🎁 Найдено ${gifts.length} активных подарков:`, gifts.map(g => ({ id: g.id, itemId: g.itemId, itemType: g.itemType })));
      setUserGifts(gifts);
      
      giftItemIdsRef.current = giftIds;
      setGiftItemIdsReady(true);
      
      return gifts;
    } catch (error) {
      console.error("Error fetching user gifts:", error);
      setGiftItemIdsReady(true);
      return [];
    }
  };

  // Загрузка курсов пользователя (индивидуальный доступ)
  const fetchUserCourses = async () => {
    if (!userId) return [];

    try {
      const collectionName = getCollectionName();
      const userDocRef = doc(db, collectionName, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        const courses = userData.courses || [];
        console.log(`📚 Индивидуальный доступ к ${courses.length} курсам (ID):`, courses);
        setUserCourses(courses);
        
        const coursesAccess = userData.coursesAccess || [];
        console.log(`📅 Найдено ${coursesAccess.length} записей в coursesAccess:`, coursesAccess.map(access => ({
          id: access.id,
          endDate: access.endDate,
          startDate: access.startDate
        })));
        setUserCoursesAccess(coursesAccess);
        
        return courses;
      }
      return [];
    } catch (error) {
      console.error("Error fetching user courses:", error);
      return [];
    }
  };

  // Загрузка тренингов пользователя (индивидуальный доступ)
  const fetchUserTrainings = async () => {
    if (!userId) return [];

    try {
      const collectionName = getCollectionName();
      const userDocRef = doc(db, collectionName, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        const trainings = userData.trainings || [];
        console.log(`📖 Индивидуальный доступ к ${trainings.length} тренингам (ID):`, trainings);
        setUserTrainings(trainings);
        
        const trainingsAccess = userData.trainingsAccess || [];
        console.log(`📅 Найдено ${trainingsAccess.length} записей в trainingsAccess:`, trainingsAccess.map(access => ({
          id: access.id,
          endDate: access.endDate,
          startDate: access.startDate
        })));
        setUserTrainingsAccess(trainingsAccess);
        
        return trainings;
      }
      return [];
    } catch (error) {
      console.error("Error fetching user trainings:", error);
      return [];
    }
  };

  // Проверка, не истек ли доступ к курсу
  const isCourseAccessExpired = (courseId) => {
    const access = userCoursesAccess.find(a => a.id === courseId);
    if (!access || !access.endDate) {
      return false;
    }
    
    const now = new Date();
    const endDate = new Date(access.endDate);
    
    if (endDate < now) {
      console.log(`⏰ Доступ к курсу ${courseId} истек ${endDate.toISOString()}`);
      return true;
    }
    
    return false;
  };

  // Проверка, не истек ли доступ к тренингу
  const isTrainingAccessExpired = (trainingId) => {
    const access = userTrainingsAccess.find(a => a.id === trainingId);
    if (!access || !access.endDate) {
      return false;
    }
    
    const now = new Date();
    const endDate = new Date(access.endDate);
    
    if (endDate < now) {
      console.log(`⏰ Доступ к тренингу ${trainingId} истек ${endDate.toISOString()}`);
      return true;
    }
    
    return false;
  };

  // Загрузка всех данных пользователя
  useEffect(() => {
    if (!userId) {
      setDataLoaded(true);
      setGiftItemIdsReady(true);
      return;
    }

    const loadUserData = async () => {
      console.log(`🔄 Загрузка индивидуальных доступов пользователя (тип: ${userType})...`);
      await Promise.all([
        fetchUserGifts(),
        fetchUserCourses(),
        fetchUserTrainings()
      ]);
      console.log("✅ Индивидуальные доступы загружены");
      setDataLoaded(true);
    };

    loadUserData();
  }, [userId, userType]);

  // ОСНОВНАЯ ФУНКЦИЯ ПРОВЕРКИ ДОСТУПА
  const hasPersonalAccess = (item, type = 'course') => {
    // Проверяем, есть ли подарок на этот материал
    const isGiftAccess = giftItemIdsRef.current.has(item.id);
    
    console.log(`🔍 Проверка доступа для: ${item.title} (${item.id}), тип: ${type}, статус: ${item.status}, isGift: ${isGiftAccess}, userType: ${userType}`);
    
    // Если нет пользователя - нет доступа
    if (!userId) {
      console.log(`❌ Нет авторизации, доступ запрещен: ${item.title}`);
      return false;
    }

    // 1. Проверка персонального доступа через подарки (игнорируем статус)
    if (isGiftAccess) {
      console.log(`✅ Персональный доступ через подарок (статус игнорируется): ${item.title}`);
      return true;
    }

    // 2. Для НЕ-подарков проверяем статус
    const validStatuses = ["published", "free"];
    if (!validStatuses.includes(item.status)) {
      console.log(`❌ Контент имеет недопустимый статус (${item.status}): ${item.title}`);
      return false;
    }

    // 3. Проверка персонального доступа через courses/trainings с проверкой срока
    if (type === 'course') {
      // Проверка для курсов
      const hasCourseInArray = userCourses.includes(item.id);
      
      if (hasCourseInArray) {
        const isExpired = isCourseAccessExpired(item.id);
        if (!isExpired) {
          console.log(`✅ Персональный доступ через courses: ${item.title}`);
          return true;
        } else {
          console.log(`❌ Персональный доступ к курсу истек: ${item.title}`);
          return false;
        }
      }
    } else {
      // Проверка для тренингов
      const hasTrainingInArray = userTrainings.includes(item.id);
      
      if (hasTrainingInArray) {
        const isExpired = isTrainingAccessExpired(item.id);
        if (!isExpired) {
          console.log(`✅ Персональный доступ через trainings: ${item.title}`);
          return true;
        } else {
          console.log(`❌ Персональный доступ к тренингу истек: ${item.title}`);
          return false;
        }
      }
    }

    // 4. Проверка персонального доступа по paidAccessId
    if (item.paidAccessId && item.paidAccessId === userId) {
      console.log(`✅ Персональный доступ по paidAccessId: ${item.title}`);
      return true;
    }

    // 5. Для веб-пользователей дополнительная проверка по email
    if (userType === 'web' && firebaseUser?.email) {
      if (item.paidAccessEmail && item.paidAccessEmail === firebaseUser.email) {
        console.log(`✅ Персональный доступ по email для веб-пользователя: ${item.title}`);
        return true;
      }
    }

    // Если ни одно из условий не выполнено - доступа нет
    console.log(`❌ Нет персонального доступа: ${item.title} (статус: ${item.status})`);
    return false;
  };

  // Загрузка тренингов и курсов
  useEffect(() => {
    // Ждем загрузки всех данных и подарков
    if (!dataLoaded || !giftItemIdsReady) return;

    const loadTrainingsAndCourses = async () => {
      try {
        setLoading(true);
        
        // Загружаем ВСЕ тренинги
        const trainingsSnap = await getDocs(collection(db, "trainings"));
        const allTrainings = trainingsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        
        console.log("📚 Все тренинги в базе:", allTrainings.map(t => ({ id: t.id, title: t.title, status: t.status, isGift: giftItemIdsRef.current.has(t.id) })));

        // Фильтруем тренинги
        const accessibleTrainings = allTrainings.filter(item => {
          // Проверяем категорию только для тренингов
          const isTrainingCategory = item.category && item.category.toLowerCase() === "обучение";
          if (!isTrainingCategory) return false;
          return hasPersonalAccess(item, 'training');
        });

        accessibleTrainings.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });

        // Загружаем ВСЕ курсы
        const coursesSnap = await getDocs(collection(db, "courses"));
        const allCourses = coursesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        
        console.log("📚 Все курсы в базе:", allCourses.map(c => ({ id: c.id, title: c.title, status: c.status, isGift: giftItemIdsRef.current.has(c.id) })));

        // Фильтруем курсы
        const accessibleCourses = allCourses.filter(item => {
          return hasPersonalAccess(item, 'course');
        });

        accessibleCourses.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });

        console.log("📊 ИТОГОВЫЕ доступные материалы:", {
          тренинги: accessibleTrainings.map(t => ({ id: t.id, title: t.title, fromGift: giftItemIdsRef.current.has(t.id) })),
          курсы: accessibleCourses.map(c => ({ id: c.id, title: c.title, fromGift: giftItemIdsRef.current.has(c.id) }))
        });

        setTrainings(accessibleTrainings);
        setCourses(accessibleCourses);
      } catch (e) {
        console.error("Ошибка загрузки данных:", e);
      } finally {
        setLoading(false);
      }
    };

    loadTrainingsAndCourses();
  }, [dataLoaded, giftItemIdsReady, userId, userCourses, userCoursesAccess, userTrainings, userTrainingsAccess, userType, firebaseUser]);

  const handleTrainingClick = (training) => {
    if (hasPersonalAccess(training, 'training')) {
      navigate(`/training/${training.id}`);
    } else {
      console.log("Доступ запрещен: нет персонального доступа");
    }
  };

  const handleCourseClick = (course) => {
    if (hasPersonalAccess(course, 'course')) {
      navigate(`/course/${course.id}`);
    } else {
      console.log("Доступ запрещен: нет персонального доступа");
    }
  };

  if (loading || userLoading || !dataLoaded || !giftItemIdsReady) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="text-white text-lg">Загрузка...</div>
        </div>
      </div>
    );
  }

  console.log("📊 Рендер страницы:", {
    trainingsCount: trainings.length,
    coursesCount: courses.length,
    userId,
    userType,
    individualTrainings: userTrainings,
    individualCourses: userCourses,
    trainingsAccess: userTrainingsAccess,
    coursesAccess: userCoursesAccess,
    gifts: userGifts,
    giftItemIds: Array.from(giftItemIdsRef.current)
  });

  return (
    <>
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1,
        }}
      />

      <div className="w-full px-4 pt-6 pb-24 relative z-10">
        <h1 className="text-white text-[24px] font-semibold mb-6">
          ОБУЧЕНИЕ
        </h1>

        {userId && (userCourses.length > 0 || userTrainings.length > 0 || userGifts.length > 0) && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/40">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <p className="text-white font-medium flex items-center">
                  🔐 Персональный доступ
                </p>
                {userType === 'telegram' ? (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-900/50 text-blue-300">
                    📱 Telegram
                  </span>
                ) : userType === 'web' ? (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-900/50 text-green-300">
                    🌐 Web
                  </span>
                ) : null}
              </div>
              <p className="text-sm text-white/70 mt-1">
                Доступ предоставляется индивидуально
              </p>
              {userCourses.length > 0 && (
                <p className="text-xs text-blue-300 mt-2 flex items-center gap-1">
                  <span>📚</span> Доступно {userCourses.length} курсов
                </p>
              )}
              {userTrainings.length > 0 && (
                <p className="text-xs text-purple-300 mt-2 flex items-center gap-1">
                  <span>📖</span> Доступно {userTrainings.length} тренингов
                </p>
              )}
              {userGifts.length > 0 && (
                <p className="text-xs text-pink-300 mt-2 flex items-center gap-1">
                  <span>🎁</span> {userGifts.length} активных подарков
                </p>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {trainings.length > 0 && trainings.map((training) => {
            return (
              <div
                key={training.id}
                className="w-full rounded-[20px] border p-4 flex items-center gap-4"
                style={{
                  background: "#241352",
                  borderColor: "rgba(255,255,255,0.2)"
                }}
              >
                <div className="w-[79px] h-[79px] rounded-[12px] overflow-hidden shrink-0">
                  <img
                    src={training.image || "/default-training.jpg"}
                    alt={training.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1513366208864-87536b8bd7b4?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80";
                    }}
                  />
                </div>

                <div className="flex-1 flex flex-col justify-between min-h-[72px]">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap pr-16">
                      <div className="text-[16px] leading-tight font-medium break-words whitespace-normal text-[#C8AFE8]">
                        {training.title}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-1">
                      <div className="text-[14px] font-medium text-[#34D3FF]">
                        Доступно
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <button
                      className="px-4 py-1.5 rounded-full text-white text-[13px] font-medium active:scale-95 transition hover:opacity-90"
                      style={{
                        background: "#6B3FD8",
                      }}
                      onClick={() => handleTrainingClick(training)}
                    >
                      Смотреть
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {courses.length > 0 && (
            <>
              {trainings.length > 0 && <div className="h-4"></div>}
              {courses.map((course) => {
                return (
                  <div
                    key={course.id}
                    className="w-full rounded-[20px] border p-4 flex items-center gap-4"
                    style={{
                      background: "#241352",
                      borderColor: "rgba(255,255,255,0.2)"
                    }}
                  >
                    <div className="w-[79px] h-[79px] rounded-[12px] overflow-hidden shrink-0">
                      <img
                        src={course.image || "/default-training.jpg"}
                        alt={course.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1513366208864-87536b8bd7b4?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80";
                        }}
                      />
                    </div>

                    <div className="flex-1 flex flex-col justify-between min-h-[72px]">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap pr-16">
                          <div className="text-[16px] leading-tight font-medium break-words whitespace-normal text-[#C8AFE8]">
                            {course.title}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1">
                          <div className="text-[14px] font-medium text-[#34D3FF]">
                            Доступно
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <button
                          className="px-4 py-1.5 rounded-full text-white text-[13px] font-medium active:scale-95 transition hover:opacity-90"
                          style={{
                            background: "#6B3FD8",
                          }}
                          onClick={() => handleCourseClick(course)}
                        >
                          Смотреть
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {trainings.length === 0 && courses.length === 0 && (
            <div className="text-center py-12 bg-white/5 rounded-2xl backdrop-blur-sm">
              <p className="text-white/60">Доступные материалы не найдены</p>
              {!userId && (
                <p className="text-white/40 text-sm mt-2">
                  Для просмотра материалов необходима авторизация
                </p>
              )}
              {userId && (
                <p className="text-white/40 text-sm mt-2">
                  У вас нет персонального доступа к материалам
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Training;